import type { VideoController, VideoEvent, ViewportClientRect, ViewportRect } from '@ports/VideoController.ts';
import type { YtNamespace, YtPlayer, YtPlayerOptions } from '../../lib/youtubeApi.ts';
import { loadYoutubeIframeApi } from '../../lib/youtubeApi.ts';

export type YoutubeIframeVideoControllerOptions = {
  container: HTMLElement;
  videoId: string;
  /** Injected YT namespace (tests). When set, skips script load. */
  yt?: YtNamespace;
  /** Host window for API load + resize listeners. */
  host?: Window & typeof globalThis;
  playerVars?: Record<string, string | number>;
};

type ListenerMap = Map<VideoEvent, Set<() => void>>;

const DEFAULT_PLAYER_VARS: Record<string, string | number> = {
  playsinline: 1,
  rel: 0,
  modestbranding: 1,
  fs: 1,
  enablejsapi: 1,
  /** Do not force-on CC; we disable native captions after ready. */
  cc_load_policy: 0,
  iv_load_policy: 3,
};

/**
 * Turn off YouTube's built-in caption renderer so only our overlay shows.
 * Best-effort — API surface varies by player build.
 */
export function disableYoutubeNativeCaptions(player: YtPlayer | null | undefined): void {
  if (!player) return;
  try {
    player.unloadModule?.('captions');
  } catch {
    // ignore
  }
  try {
    player.setOption?.('captions', 'track', {});
  } catch {
    // ignore
  }
}

/**
 * VideoController backed by the YouTube IFrame Player API.
 * `timeupdate` is a no-op — subtitle sync polls getCurrentTimeMs via rAF (M5).
 */
export class YoutubeIframeVideoController implements VideoController {
  private player: YtPlayer | null = null;
  private ready = false;
  private disposed = false;
  private readonly listeners: ListenerMap = new Map();
  private resizeObserver: ResizeObserver | null = null;
  private readonly host: Window & typeof globalThis;
  private readonly container: HTMLElement;
  private seeking = false;
  private seekTargetMs = 0;
  private seekPollId: ReturnType<typeof setInterval> | null = null;
  private seekFallbackId: ReturnType<typeof setTimeout> | null = null;
  private readonly onWindowResize = (): void => this.emit('resize');
  private readonly onOrientation = (): void => this.emit('resize');

  constructor(private readonly options: YoutubeIframeVideoControllerOptions) {
    this.container = options.container;
    this.host = options.host ?? window;
  }

  /** Create the YT.Player. Resolves when onReady fires. */
  async init(): Promise<void> {
    if (this.disposed) throw new Error('YoutubeIframeVideoController disposed');
    if (this.player) return;

    const yt = this.options.yt ?? (await this.loadYt());
    const playerVars = {
      ...DEFAULT_PLAYER_VARS,
      origin: this.host.location?.origin ?? '',
      ...this.options.playerVars,
    };

    await new Promise<void>((resolve, reject) => {
      const opts: YtPlayerOptions = {
        videoId: this.options.videoId,
        playerVars,
        events: {
          onReady: (event) => {
            this.player = event.target;
            this.ready = true;
            disableYoutubeNativeCaptions(this.player);
            this.wireResize();
            resolve();
          },
          onStateChange: (event) => this.handleStateChange(event.data, yt),
        },
      };

      try {
        // Always mount into a dedicated child. YT.Player replaces its target node with an
        // iframe — if we pass the React-managed container, the ref detaches and onReady /
        // re-init hang ("Loading player…" forever) while the iframe may still play.
        this.container.replaceChildren();
        const mount = this.host.document.createElement('div');
        mount.id = `yt-embed-${Math.random().toString(36).slice(2)}`;
        mount.style.width = '100%';
        mount.style.height = '100%';
        this.container.appendChild(mount);
        this.player = new yt.Player(mount, opts);
      } catch (err) {
        reject(err);
      }
    });
  }

  private async loadYt(): Promise<YtNamespace> {
    await loadYoutubeIframeApi({ host: this.host });
    const yt = this.host.YT;
    if (!yt?.Player) throw new Error('YouTube IFrame API unavailable');
    return yt;
  }

  private wireResize(): void {
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.emit('resize'));
      this.resizeObserver.observe(this.container);
    }
    this.host.addEventListener('resize', this.onWindowResize);
    this.host.addEventListener('orientationchange', this.onOrientation);
  }

  private handleStateChange(data: number, yt: YtNamespace): void {
    const { PLAYING, PAUSED, ENDED, BUFFERING } = yt.PlayerState;
    // Re-assert native CC off — user may re-enable via the iframe CC button.
    if (data === PLAYING || data === PAUSED || data === BUFFERING) {
      disableYoutubeNativeCaptions(this.player);
    }
    if (data === PLAYING) {
      this.finishSeekIfPending();
      this.emit('play');
    } else if (data === PAUSED || data === ENDED) {
      // Seek-while-paused often lands in PAUSED without ever hitting PLAYING.
      if (data === PAUSED) this.finishSeekIfPending();
      this.emit('pause');
    } else if (data === BUFFERING && this.seeking) {
      this.emit('seeking');
    }
  }

  private clearSeekWatchers(): void {
    if (this.seekPollId != null) {
      clearInterval(this.seekPollId);
      this.seekPollId = null;
    }
    if (this.seekFallbackId != null) {
      clearTimeout(this.seekFallbackId);
      this.seekFallbackId = null;
    }
  }

  /** Emit seeked once after seekTo — works while paused (shadowing deferred scrub). */
  private finishSeekIfPending(): void {
    if (!this.seeking) return;
    this.seeking = false;
    this.clearSeekWatchers();
    this.emit('seeked');
  }

  private armSeekWatchers(): void {
    this.clearSeekWatchers();
    this.seekPollId = setInterval(() => {
      if (!this.seeking) return;
      if (Math.abs(this.getCurrentTimeMs() - this.seekTargetMs) <= 250) {
        this.finishSeekIfPending();
      }
    }, 50);
    // YT may never change state after seekTo while paused.
    this.seekFallbackId = setTimeout(() => {
      this.finishSeekIfPending();
    }, 1500);
  }

  private emit(event: VideoEvent): void {
    const set = this.listeners.get(event);
    if (!set) return;
    for (const cb of [...set]) cb();
  }

  getCurrentTimeMs(): number {
    if (!this.ready || !this.player) return 0;
    const t = this.player.getCurrentTime();
    if (!Number.isFinite(t)) return 0;
    return t * 1000;
  }

  getDurationMs(): number {
    if (!this.ready || !this.player) return 0;
    const t = this.player.getDuration();
    if (!Number.isFinite(t)) return 0;
    return t * 1000;
  }

  isPaused(): boolean {
    if (!this.ready || !this.player) return true;
    const yt = this.options.yt ?? this.host.YT;
    if (!yt) return true;
    return this.player.getPlayerState() !== yt.PlayerState.PLAYING;
  }

  play(): void {
    this.player?.playVideo();
  }

  pause(): void {
    this.player?.pauseVideo();
  }

  seekMs(ms: number): void {
    if (!this.player) return;
    this.seeking = true;
    this.seekTargetMs = ms;
    this.emit('seeking');
    this.player.seekTo(ms / 1000, true);
    this.armSeekWatchers();
  }

  getViewportRect(): ViewportRect {
    const el = this.player?.getIframe?.() ?? this.container;
    const rect = el.getBoundingClientRect();
    return { width: rect.width, height: rect.height };
  }

  getPlaybackClientRect(): ViewportClientRect {
    const el = this.player?.getIframe?.() ?? this.container;
    const rect = el.getBoundingClientRect();
    return {
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height,
    };
  }

  /** YouTube video title from IFrame API (empty when unavailable / mocks). */
  getVideoTitle(): string {
    if (!this.ready || !this.player) return '';
    try {
      const title = this.player.getVideoData?.()?.title;
      return typeof title === 'string' ? title.trim() : '';
    } catch {
      return '';
    }
  }

  on(event: VideoEvent, cb: () => void): () => void {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(cb);
    return () => {
      set?.delete(cb);
    };
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.ready = false;
    this.seeking = false;
    this.clearSeekWatchers();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.host.removeEventListener('resize', this.onWindowResize);
    this.host.removeEventListener('orientationchange', this.onOrientation);
    this.listeners.clear();
    try {
      this.player?.destroy();
    } catch {
      // ignore destroy errors
    }
    this.player = null;
  }
}
